/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ClientSupport } from '../models/ClientSupport'
import type { Error } from '../models/Error'
import type { ProductType } from '../models/ProductType'
import type { RequestAdminUpdateCampaign } from '../models/RequestAdminUpdateCampaign'
import type { RequestChangeCampaignLockedStatus } from '../models/RequestChangeCampaignLockedStatus'
import type { ResponseAdminCampaignDetails } from '../models/ResponseAdminCampaignDetails'
import type { ResponseAdminCampaigns } from '../models/ResponseAdminCampaigns'
import type { ResponseGenericCampaign } from '../models/ResponseGenericCampaign'
import type { ResponseWorkflowList } from '../models/ResponseWorkflowList'

import type { CancelablePromise } from '../core/CancelablePromise'
import { OpenAPI } from '../core/OpenAPI'
import { request as __request } from '../core/request'

export class AdminCampaignService {
  /**
   * Zmienia status blokady kampanii (zablokowanie/odblokowanie edycji przez klienta)
   *
   * @returns any Status blokady kampanii został zmieniony
   * @returns Error unexpected error
   * @throws ApiError
   */
  public static changeCampaignLockedStatus({
    campaignId,
    requestBody,
  }: {
    campaignId: number
    /**
     * Status blokady kampanii
     */
    requestBody: RequestChangeCampaignLockedStatus
  }): CancelablePromise<any | Error> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/campaign/{campaignId}/lock',
      path: {
        campaignId: campaignId,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * Zwraca listę kampanii przypisanych do admina
   *
   * @returns ResponseAdminCampaigns dane kampanii wraz podstawowymi danymi klienta
   * @returns Error unexpected error
   * @throws ApiError
   */
  public static getAdminCampaigns({
    search,
    productType,
    page,
    limit,
    status,
    sortBy = 'name',
    onlyArchive,
    advOrderOwnerId,
    support,
    sortDir,
    perfoAdminId,
  }: {
    /**
     * napis po którym będą filtrowane wyniki (nazwy klientów)
     */
    search: string
    productType: Array<ProductType>
    /**
     * numer strony do pobrania
     */
    page: number
    /**
     * liczba elementów do pobrania
     */
    limit: number
    /**
     * oczekiwany status kampanii
     */
    status?:
      | 'INCOMPLETE'
      | 'WAIT_FOR_CONFIRMATION'
      | 'STARTED'
      | 'PAUSED'
      | 'ENDED'
      | 'WAIT_FOR_START_DATE'
    /**
     * pole po którym będą sortowane wyniki zapytania
     */
    sortBy?:
      | 'name'
      | 'client_name'
      | 'shop_name'
      | 'type'
      | 'date_start'
      | 'status'
    onlyArchive?: boolean
    /**
     * właściciel zlecenia reklamowego
     */
    advOrderOwnerId?: number
    /**
     * kto obsługuje klienta
     */
    support?: ClientSupport
    /**
     * kierunek sortowania
     */
    sortDir?: 'asc' | 'desc'
    /**
     * identyfikator admina perfo
     */
    perfoAdminId?: number
  }): CancelablePromise<ResponseAdminCampaigns | Error> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/admin/campaign',
      query: {
        search: search,
        status: status,
        sortBy: sortBy,
        onlyArchive: onlyArchive,
        advOrderOwnerId: advOrderOwnerId,
        support: support,
        productType: productType,
        sortDir: sortDir,
        page: page,
        limit: limit,
        perfoAdminId: perfoAdminId,
      },
    })
  }

  /**
   * zwraca informacje o wybranej kampanii
   *
   * @returns any infomacje o kampanii
   * @returns Error unexpected error
   * @throws ApiError
   */
  public static adminGetCampaign({
    campaignId,
  }: {
    campaignId: number
  }): CancelablePromise<
    (ResponseAdminCampaignDetails | ResponseGenericCampaign) | Error
  > {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/admin/campaign/{campaignId}',
      path: {
        campaignId: campaignId,
      },
    })
  }

  /**
   * Zwraca workflow wybranych kampanii
   *
   * @returns ResponseWorkflowList lista workflow
   * @throws ApiError
   */
  public static adminGetWorkflows({
    campaignIds,
  }: {
    /**
     * identyfikatory kampanii
     */
    campaignIds?: Array<number>
  }): CancelablePromise<ResponseWorkflowList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/admin/campaign/workflows',
      query: {
        campaignIds: campaignIds,
      },
    })
  }

  /**
   * Zwraca listę przypiętych kampanii admina
   *
   * @returns number lista przypiętych kampanii admina
   * @returns Error unexpected error
   * @throws ApiError
   */
  public static getPinnedCampaigns(): CancelablePromise<Array<number> | Error> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/admin/campaign/pinned-list/',
    })
  }

  /**
   * Przypina kampanię
   *
   * @returns Error unexpected error
   * @throws ApiError
   */
  public static pinCampaign({
    campaignId,
  }: {
    /**
     * przypiecie kampanii o podanym ID
     */
    campaignId: string
  }): CancelablePromise<Error> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/admin/campaign/{campaignId}/pin',
      path: {
        campaignId: campaignId,
      },
    })
  }

  /**
   * Oznacza jako sprawdzoną przez perfo(akcja cykliczna)
   *
   * @returns Error unexpected error
   * @throws ApiError
   */
  public static adminConfirmCampaign({
    campaignId,
  }: {
    campaignId: number
  }): CancelablePromise<Error> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/admin/campaign/{campaignId}/confirmation',
      path: {
        campaignId: campaignId,
      },
    })
  }

  /**
   * ustawia pola kampanii specyficzne dla administratora
   *
   * @returns Error unexpected error
   * @throws ApiError
   */
  public static adminUpdateCampaignData({
    campaignId,
    requestBody,
  }: {
    campaignId: number
    /**
     * Dane kampanii
     */
    requestBody: RequestAdminUpdateCampaign
  }): CancelablePromise<Error> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/admin/campaign/{campaignId}/admin-data/',
      path: {
        campaignId: campaignId,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }
}
